import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ServiceInfoSection = ({ formData, setFormData, errors, currentServiceIndex }) => {
  const currentService = formData?.services?.[currentServiceIndex];

  const updateCurrentService = (field, value) => {
    setFormData(prev => ({
      ...prev,
      services: prev?.services?.map((service, index) => 
        index === currentServiceIndex 
          ? { ...service, [field]: value }
          : service
      )
    }));
  };

  const categoryOptions = [
    { value: '', label: 'Select Category' },
    { value: 'yoga-movement', label: 'Yoga & Movement' },
    { value: 'meditation-mindfulness', label: 'Meditation & Mindfulness' },
    { value: 'energy-healing', label: 'Energy Healing' },
    { value: 'massage-bodywork', label: 'Massage & Bodywork' },
    { value: 'nutrition-wellness', label: 'Nutrition & Wellness' },
    { value: 'spiritual-practices', label: 'Spiritual Practices' },
    { value: 'holistic-therapy', label: 'Holistic Therapy' },
    { value: 'workshops-courses', label: 'Workshops & Courses' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Service Information
            </h2>
            <p className="text-muted-foreground text-sm">
              Tell us about your holistic service - Service {currentServiceIndex + 1}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Service Name */}
          <div className="md:col-span-2">
            <Input
              label="Service Name"
              placeholder="e.g., Full Moon Kundalini Session"
              value={currentService?.name || ''}
              onChange={(e) => updateCurrentService('name', e?.target?.value)}
              error={errors?.name}
              required
            />
          </div>

          {/* Business Name */}
          <div className="md:col-span-1">
            <Input
              label="Business Name"
              placeholder="e.g., House of Kundalini"
              value={currentService?.businessName || ''}
              onChange={(e) => updateCurrentService('businessName', e?.target?.value)}
              error={errors?.businessName}
              required
            />
          </div>

          {/* Category */}
          <div className="md:col-span-1">
            <Select
              label="Category"
              value={currentService?.category || ''}
              onChange={(value) => updateCurrentService('category', value)}
              options={categoryOptions}
              error={errors?.category}
              required
            />
          </div>

          {/* Short Description */}
          <div className="md:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Short Description
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                rows={2}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
                  errors?.shortDescription 
                    ? 'border-destructive focus:border-destructive focus:ring-destructive/20' :'border-border'
                }`}
                placeholder="Brief description of your service (max 150 characters)"
                value={currentService?.shortDescription || ''}
                onChange={(e) => updateCurrentService('shortDescription', e?.target?.value)}
                maxLength={150}
              />
              <div className="flex justify-between items-center">
                {errors?.shortDescription && (
                  <span className="text-sm text-destructive">{errors?.shortDescription}</span>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  {(currentService?.shortDescription || '')?.length}/150
                </span>
              </div>
            </div>
          </div>

          {/* Full Description */}
          <div className="md:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Full Description
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
                  errors?.fullDescription 
                    ? 'border-destructive focus:border-destructive focus:ring-destructive/20' :'border-border'
                }`}
                placeholder="Detailed description of what participants can expect from this service..."
                value={currentService?.fullDescription || ''}
                onChange={(e) => updateCurrentService('fullDescription', e?.target?.value)}
              />
              {errors?.fullDescription && (
                <span className="text-sm text-destructive">{errors?.fullDescription}</span>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="md:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Tags
                <span className="text-muted-foreground text-xs ml-1">(optional)</span>
              </label>
              <Input
                placeholder="e.g., kundalini, meditation, full moon, group session"
                value={currentService?.tags || ''}
                onChange={(e) => updateCurrentService('tags', e?.target?.value)}
              />
              <p className="text-xs text-muted-foreground">
                Add relevant tags to help people discover your service (separate with commas)
              </p>
            </div>
          </div>
        </div>

        {/* Service Benefits */}
        <div className="mt-8 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="Star" size={12} className="text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground text-sm">Service Pricing</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Each service is £9.99/year and can be booked independently. You can add up to 5 services per business.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceInfoSection;